import React, { useCallback, useEffect } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import useHoverNavigation from '../use-hover-navigation';
import { hoverHighlightSx } from '../hover-style';
import { useConnect4Socket } from './connect4-provider';

const ITEMS_COUNT = 2; // Cancel, Back

const barSx = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    userSelect: 'none',
    transition: 'box-shadow 0.3s ease, background-color 0.3s ease',
    borderRadius: 4,
    margin: 2,
    gap: 1,
};

export default function QueueScreen({ playerName, onCancel, onBack }) {
    const { t } = useTranslation();
    const socket = useConnect4Socket();

    useEffect(() => {
        if (socket) {
            socket.emit('queue:join', { name: playerName });
        }
        return () => {
            if (socket) socket.emit('queue:leave');
        };
    }, [socket, playerName]);

    const onSelect = useCallback((index) => {
        if (index === 0) onCancel();
        else onBack();
    }, [onCancel, onBack]);

    const { active, activeIndex } = useHoverNavigation(ITEMS_COUNT, onSelect);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%' }}>
            {/* Main area — 80% */}
            <Box sx={{ flex: '1 1 80%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 3 }}>
                <CircularProgress size={64} />
                <Typography variant="h4">
                    {t('APPLICATIONS.CONNECT4.FINDING_GAME')}
                </Typography>
                <Typography variant="subtitle1" color="text.secondary">
                    {t('APPLICATIONS.CONNECT4.WAITING_OPPONENT')}
                </Typography>
            </Box>

            {/* Bottom bar — 20% */}
            <Box sx={{ flex: '0 0 20%', display: 'flex', gap: 1, mx: 2, mb: 2 }}>
                <Box
                    onClick={onCancel}
                    sx={{
                        ...barSx,
                        flex: 1,
                        bgcolor: 'warning.light',
                        color: 'warning.contrastText',
                        ...(active && activeIndex === 0 && hoverHighlightSx),
                    }}
                >
                    <CloseIcon sx={{ fontSize: 32 }} />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {t('APPLICATIONS.CONNECT4.CANCEL')}
                    </Typography>
                </Box>
                <Box
                    onClick={onBack}
                    sx={{
                        ...barSx,
                        flex: 1,
                        bgcolor: 'grey.300',
                        color: 'text.primary',
                        ...(active && activeIndex === 1 && hoverHighlightSx),
                    }}
                >
                    <ArrowBackIcon sx={{ fontSize: 32 }} />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {t('APPLICATIONS.BACK')}
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
}
